import { asyncOperation, clientConfig, clientRepo, toastStore, useUser } from "@zickko/client";
import { LexicalEditor } from "../lexical/LexicalEditor";
import { useOrg } from "../providers/OrgProvider";
import { useEffect, useState } from "react";
import { OutboundEmail } from "@zickko/shared/src/models/OutboundEmail";
import { EmailKey, GcsFile, gcsPaths, idFactory } from "@zickko/shared";
import { writeFileToGcs } from "@zickko/client/src/gcs/writeFileToGcs";
import toast from "react-hot-toast";
import { Status } from "@zickko/shared/src/models/Status";
import { writeStringToGcs } from "@zickko/client/src/gcs/writeStringToGcs";
import { DraftAttachedFile, DraftEmail } from "@zickko/shared/src/models/DraftEmail";
import debounce from "lodash/debounce";
import { getDownloadURL, getStorage, ref } from "firebase/storage";

export enum Mode {
  Compose = "compose",
  Drafts = "drafts",
}

type Props = {
  mode: Mode;
  currentDraft?: DraftEmail;
};

export const ComposeWidget = (props: Props) => {
  const { org } = useOrg();
  const { user } = useUser();

  const [draftEmail, setDraftEmail] = useState<Partial<DraftEmail>>({
    text: null,
    to: null,
    subject: null,
    attachedFiles: [],
  });

  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [draftEmailId, setDraftEmailId] = useState<string | null>(null);
  const [busyFlag, setBusyFlag] = useState(false);
  const [editorHasContent, setEditorHasContent] = useState(false);

  // Populate fields from draft data or initialize new draft
  useEffect(() => {
    if (props.mode === Mode.Drafts && props.currentDraft) {
      // Load current draft data.
      setDraftEmailId(props.currentDraft.id);
      setDraftEmail({
        to: props.currentDraft.to || null,
        subject: props.currentDraft.subject || null,
        text: props.currentDraft.text || null,
        attachedFiles: props.currentDraft.attachedFiles || [],
      });
    } else if (
      props.mode === Mode.Compose &&
      !draftEmailId &&
      (draftEmail.subject || draftEmail.to || editorHasContent)
    ) {
      const newDraftEmailId = idFactory.createId();
      setDraftEmailId(newDraftEmailId);

      const emailKey: EmailKey = { orgId: org.id, uid: user.uid, emailId: newDraftEmailId };

      const placeholderHtmlContent: GcsFile = {
        path: gcsPaths.outboundEmailHtmlContentPath({ ...emailKey }),
        id: idFactory.createId(),
        bucket: clientConfig.storageBucket,
        filename: "placeholder.html",
      };

      initializeDraftEmail(newDraftEmailId, placeholderHtmlContent, []);
    }
  }, [props.mode, props.currentDraft, editorHasContent, draftEmailId]);

  // Auto-save draft functionality
  useEffect(() => {
    if (!draftEmailId) return;
    const debouncedSave = debounce(saveDraft, 1000);
    debouncedSave();
    return () => debouncedSave.cancel();
  }, [draftEmail.text, draftEmail.attachedFiles, draftEmail.to, draftEmail.subject, editorHasContent]);

  const initializeDraftEmail = async (draftId: string, htmlContent: GcsFile, attachments?: DraftAttachedFile[]) => {
    const initialDraft: DraftEmail = {
      id: draftId,
      uid: user.uid,
      orgId: org.id,
      from: user.email,
      to: draftEmail.to,
      subject: draftEmail.subject,
      htmlContentGcsFile: htmlContent,
      text: "",
      attachedFiles: attachments || [],
      lastModified: new Date().toISOString(),
    };

    await asyncOperation({
      fn: () => clientRepo.draftEmail.set({ orgId: org.id, uid: user.uid, emailId: draftId }, initialDraft),
      onError: (e) => console.error("Error initializing draft:", e),
    });
  };

  const uploadAttachments = async (files: File[]) => {
    const transformedFiles = files.map((file) => transformRawFileToAttachment(file, org.id, user.uid, draftEmailId));

    const newDraftAttachments = transformedFiles.filter(
      (file) =>
        !draftEmail.attachedFiles.some(
          (uploadedAttachments) => uploadedAttachments.gcsFile.filename === file.gcsFile.filename
        )
    );

    const uploadPromises = newDraftAttachments.map(async (attachment) => {
      const newUploadedFile = files.find((file) => file.name === attachment.gcsFile.filename);

      if (!newUploadedFile) return;

      try {
        await writeFileToGcs({
          file: newUploadedFile,
          path: attachment.gcsFile.path,
          bucket: clientConfig.storageBucket,
          onProgress: (percentage) => {
            setUploadProgress((prev) => ({
              ...prev,
              [newUploadedFile.name]: percentage,
            }));
          },
        });
      } catch (error) {
        console.error(`Error uploading file ${newUploadedFile.name}:`, error);
        toast.error(`Failed to upload attachment: ${newUploadedFile.name}`);
      }
    });

    await Promise.all(uploadPromises);

    setDraftEmail((prev) => ({
      ...prev,
      attachedFiles: [...(prev.attachedFiles || []), ...newDraftAttachments],
    }));
  };

  // Save draft with attachment handling
  const saveDraft = async () => {
    if (!draftEmailId) return;

    const updatedDraft: Partial<DraftEmail> = {
      text: draftEmail.text,
      attachedFiles: [...draftEmail.attachedFiles],
      lastModified: new Date().toISOString(),
      to: draftEmail.to,
      subject: draftEmail.subject,
    };

    await asyncOperation({
      fn: () => clientRepo.draftEmail.merge({ orgId: org.id, uid: user.uid, emailId: draftEmailId }, updatedDraft),
      onError: (error) => toast.error("Failed to save draft."),
    });
  };

  // Send email process
  const onSend = async () => {
    const outboundEmailId = draftEmailId || idFactory.createId();

    await asyncOperation({
      fn: async () => {
        // Upload HTML content
        const htmlContent = await writeStringToGcs({
          contents: draftEmail.text,
          gcsBucket: clientConfig.storageBucket,
          gcsFolder: gcsPaths.outboundEmailHtmlContentPath({
            orgId: org.id,
            uid: user.uid,
            emailId: outboundEmailId,
          }),
          fileExtension: "html",
          contentType: "html",
          filename: "html_content",
        });

        // Create outbound email entity
        const outboundEmail: OutboundEmail = {
          id: outboundEmailId,
          uid: user.uid,
          orgId: org.id,
          from: user.email,
          to: draftEmail.to,
          subject: draftEmail.subject,
          status: Status.failure,
          htmlContent: htmlContent,
          attachedFiles: draftEmail.attachedFiles,
        };

        await clientRepo.outboundEmail.set({ uid: user.uid, orgId: org.id, emailId: outboundEmailId }, outboundEmail);

        // Delete draft after sending
        await clientRepo.draftEmail.remove({ orgId: org.id, uid: user.uid, emailId: draftEmailId });
      },
      loadingMessage: "Sending email...",
      setBusyFlag,
      onSuccess: () => {
        toast.success("Email sent successfully!");
      },
      onError: (error) => {
        console.error("Error sending reply:", error);
        toast.error("Error sending reply");
      },
    });
  };

  // Download attachment method
  const onDownloadAttachment = (attachment: DraftAttachedFile) => {
    const storage = getStorage();
    const pathReference = ref(storage, attachment.gcsFile.path);

    asyncOperation({
      fn: async () => {
        const url = await getDownloadURL(pathReference);
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        return { blobUrl, filename: attachment.gcsFile.filename || "file" };
      },
      onSuccess: ({ blobUrl, filename }) => {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.style.display = "none";
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      },
      onError: (error) => {
        console.error("Error downloading attachment:", error);
        toastStore.error("Error downloading attachment.");
      },
      loadingMessage: "Downloading attachment...",
    });
  };

  // Render attachments method
  const renderAttachments = () => {
    if (!draftEmail.attachedFiles || draftEmail.attachedFiles.length === 0 || props.mode === Mode.Compose) {
      return null;
    }
    return (
      <div>
        <h2>Attachments</h2>
        <div className="attachments-div">
          {draftEmail.attachedFiles.map((attachment: DraftAttachedFile, index: number) => (
            <div
              key={attachment.gcsFile.id}
              style={{ textAlign: "center" }}
              onClick={() => onDownloadAttachment(attachment)}
            >
              <span className="email-attachment-text">{`Attachment ${index + 1}: ${attachment.gcsFile.filename}`}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Transform file to attachment

  const transformRawFileToAttachment = (
    file: File,
    orgId: string,
    uid: string,
    emailId: string | null
  ): DraftAttachedFile => ({
    gcsFile: {
      path: gcsPaths.outboundAttachmentPath({
        orgId,
        uid,
        emailId: emailId || idFactory.createId(),
        attachmentId: idFactory.createId(),
      }),
      id: idFactory.createId(),
      bucket: clientConfig.storageBucket,
      filename: file.name,
    },
    contentType: file.type,
    contentDisposition: "attachment",
    size: file.size,
  });

  return (
    <>
      <div>
        {props.mode === Mode.Drafts && <h3>Edit Draft</h3>}
        <div className="recipient-input">
          <input
            type="email"
            value={draftEmail.to || ""}
            onChange={(e) => setDraftEmail((prev) => ({ ...prev, to: e.target.value }))}
            placeholder="Enter recipient's email"
            required
          />
        </div>
        <div className="subject">
          <input
            type="text"
            value={draftEmail.subject || ""}
            onChange={(e) => setDraftEmail((prev) => ({ ...prev, subject: e.target.value }))}
            placeholder="Enter subject"
          />
        </div>
        <LexicalEditor
          initialHtml={props.mode === Mode.Drafts && draftEmail.text ? `<p>${draftEmail.text}</p>` : ""}
          onChange={(text) => {
            setDraftEmail((prev) => ({ ...prev, text }));
            setEditorHasContent(!!text.trim());
          }}
        />
        {renderAttachments()}
      </div>

      <div className="attachments-section">
        <input
          type="file"
          multiple
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            uploadAttachments(files);
          }}
        />
        <div className="attachments-progress">
          {draftEmail.attachedFiles.map((file) => (
            <div key={file.gcsFile.filename}>
              {file.gcsFile.filename}: {uploadProgress[file.gcsFile.filename]?.toFixed(2) || 0}% uploaded
            </div>
          ))}
        </div>
        <button className="send-button" onClick={onSend} disabled={!draftEmail.text || !draftEmail.to || busyFlag}>
          Send
        </button>
      </div>
    </>
  );
};
